import {
  Badge,
  BadgeProps,
  Box,
  Dialog,
  Grid,
  IconButton,
  styled,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import CustomDataGrid from '../../../../../common/components/customized/customdatagrid/CustomDataGrid';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import AddVehicle from './components/AddVehicle';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import { autoGetVechicleAction } from '../../../redux/reducer/autoPlannerSlices/autoplannerConfigurationSlice';
import { debounce } from 'lodash';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import { useForm } from 'react-hook-form';
import {
  deactivateVehicleAction,
  vehicleDeleteAction
} from './components/vehicleOnboard';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import constant from '../../../../../utils/constants';
import ConfirmationPopup from '../../../../../common/components/confirmationpopup/ConfirmationPopup';

interface PaginationModel {
  page: number;
  pageSize: number;
}

const VehicleOnboard = () => {
  const [vechileRows, setVechileRows] = useState<any[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pageSize, setPageSize] = useState(10);
  const [payload, setPayload] = useState<any>({ pageNo, pageSize });
  const [open, setOpen] = useState<boolean>(false);
  const [drawer, setDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [filter, setFilter] = useState('');
  const [isDialog, setIsDialog] = useState('');
  const [sortModel, setSortModel] = useState<any>([]);
  const [selected, setSelected] = useState(true);

  const vechileData = useAppSelector(state => state.vechileconfiguration);
  const isLoading = useAppSelector(state => state.DeleteAutoVehicle.isLoading);
  const { data } = useAppSelector(state => state.auth);
  const { isLoading: deactivateLoading } = useAppSelector(state => state.activateVehicle);
  const { isLoading: deleteLoading } = useAppSelector(state => state.DeleteAutoVehicle);

  let roletype = data?.role;

  const dispatch = useAppDispatch();
  const { control } = useForm();

  const APoperator = roletype === 'ROLE_OPERATOR';

  const isAPadminOrOperator =
    roletype === 'ROLE_AUTOPLANNER_ADMIN' || roletype === 'ROLE_OPERATOR';

  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }: any) => ({
    '& .MuiBadge-badge': {
      right: -4,
      top: 10,
      border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
      padding: '0 6px',
      fontSize: '11px',
      fontWeight: 600,
      backgroundColor: '#3239ea',
      color: '#fff',
      boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)'
    }
  }));

  const handleMenuClick = (row: any) => {
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateVehicle = () => {
    setOpen(true);
    handleMenuClose();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      getActions: (params: any) => {
        const row = params?.row;
        return [
          <GridActionsCellItem
            disabled={row?.isActive === 0 ? true : false}
            icon={<Icon icon='ic:baseline-edit' className='menu-icon update' />}
            label='Update'
            onClick={() => {
              handleMenuClick(params.row as any);
              handleUpdateVehicle();
            }}
            showInMenu
          />,
          row?.isActive === 0 ? (
            <GridActionsCellItem
              icon={<CustomIconButton showTooltip={false} category='Active' />}
              label='Activate'
              onClick={() => {
                setIsDialog('dialog');
                setSelectedRow(row);
              }}
              showInMenu
            />
          ) : (
            <GridActionsCellItem
              icon={<CustomIconButton showTooltip={false} category='Deactive' />}
              label='Deactivate'
              onClick={() => {
                setIsDialog('dialog');
                setSelectedRow(row);
              }}
              showInMenu
            />
          ),
          <GridActionsCellItem
            disabled={row?.isActive === 0 ? true : false}
            key='delete'
            icon={
              <Icon
                icon='ic:outline-delete'
                className='menu-icon'
                style={{ color: '#FF4343' }}
              />
            }
            label={constant.Delete}
            onClick={() => {
              handleMenuClick(params.row);
              setIsDialog('Delete');
              handleMenuClose();
            }}
            showInMenu
          />
        ];
      }
    },

    {
      field: 'isActive',
      headerName: 'Active',
      flex: 1,
      minWidth: 100,
      sortable: false,
      maxWidth: 100,
      renderCell: (params: any) => (
        <IconButton
          onClick={(event: any) => {
            event.stopPropagation();
            setIsDialog('dialog');
            setSelected(true);
            setSelectedRow(params?.row);
            handleClose();
          }}
        >
          {params?.row?.isActive === 1 ? (
            <Icon
              icon='carbon:circle-filled'
              style={{ color: '#0EBC93', fontSize: '16px' }}
              onClick={event => {
                event.stopPropagation();
                setIsDialog('dialog');
                setSelected(true);
                setSelectedRow(params?.row);
                handleClose();
              }}
            />
          ) : (
            <Icon
              icon='carbon:circle-filled'
              style={{ color: '#FF2532', fontSize: '16px' }}
              onClick={event => {
                event.stopPropagation();
                setIsDialog('dialog');
                setSelected(true);
                setSelectedRow(params?.row);
                handleClose();
              }}
            />
          )}
        </IconButton>
      )
    },
    {
      field: 'isTracking',
      headerName: 'Tracking Status',
      flex: 1,
      sortable: false,
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <Icon
          icon={row?.isTracking ? 'simple-icons:ticktick' : 'zondicons:close-outline'}
          style={{ color: row?.isTracking ? '#0EBC93' : '#FF2532', fontSize: '16px' }}
        />
      )
    },
    {
      field: 'vehicleNumber',
      headerName: 'Vehicle Number',
      width: 150
    },
    {
      field: 'vehicleType',
      headerName: 'Vehicle Type',
      width: 100,
      renderCell: ({ value }: any) => value?.toUpperCase()
    },
    {
      field: 'tripModesJoin',
      headerName: 'Tour Modes',
      width: 250
    },
    {
      field: 'absoluteSeating',
      headerName: 'Absolute Seating',
      width: 150
    },
    {
      field: 'preferredSeating',
      headerName: 'Preferred Seating',
      width: 150
    },
    {
      field: 'standardSpeedLimit',
      headerName: 'Standard Speed Limit',
      width: 150
    },
    {
      field: 'viewdriver',
      headerName: 'Driver Details',
      minWidth: 100,
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const count = params?.row?.driverDetails?.length;
        return (
          <>
            {count > 0 ? (
              <StyledBadge badgeContent={count} color='primary'>
                <Box
                  className='view-driver'
                  onClick={event => {
                    event.stopPropagation();
                    handleDriverDetails(params?.row);
                  }}
                >
                  <Icon icon='healthicons:truck-driver' className='view-driver-icon' />
                  <Typography className='view-driver-text'>View</Typography>
                </Box>
              </StyledBadge>
            ) : (
              <Typography className='no-driver-assigned'>No Driver</Typography>
            )}
          </>
        );
      }
    }
  ]?.map((item: any) => ({
    ...item,
    headerAlign: ['isTracking']?.includes(item?.field) ? 'center' : 'left',
    align: ['isTracking']?.includes(item?.field) ? 'center' : 'left'
  }));

  const handleFilterChange = useCallback(
    debounce((event: string) => {
      setFilter(event);
      const payloads = {
        ...payload,
        pageNo: 1,
        pageSize: pageSize,
        vehicleNumber: event
      };
      setPayload(payloads);
      dispatch(autoGetVechicleAction(payloads));
    }, 500),
    [pageSize, payload]
  );

  const handlePaginationModelChange = (newPaginationModel: PaginationModel) => {
    const { pageSize: newPageSize, page: newPageNo } = newPaginationModel;
    setPageSize(newPageSize);
    setPageNo(newPageNo + 1);
    const newPage = newPageNo + 1;
    const params = {
      ...payload,
      pageNo: newPage,
      pageSize: newPageSize
    };
    setPayload(params);
  };

  const handleSortModelChange = (model: any) => {
    setSortModel(model);
    const payloads = {
      ...payload,
      sortBy: model[0]?.sort,
      sortByField: model[0]?.field === 'tripModesJoin' ? 'tripModes' : model[0]?.field
    };
    setPayload(payloads);
  };

  const handleDriverDetails = (row: any) => {
    setSelectedRow(row);
    setDrawer(true);
  };

  const handleDeactivate = async () => {
    await dispatch(deactivateVehicleAction(selectedRow.vehicleNumber));
    setPageNo(pageNo);
    setPageSize(pageSize);
    setIsDialog('');
    await dispatch(autoGetVechicleAction(payload));
  };

  const handleDeleteVehicle = async () => {
    const payloads = { vehicleNumber: selectedRow.vehicleNumber };
    await dispatch(vehicleDeleteAction(payloads));
    setIsDialog('');
    await dispatch(autoGetVechicleAction(payload));
  };

  useEffect(() => {
    if (payload) dispatch(autoGetVechicleAction(payload));
  }, [payload]);

  useEffect(() => {
    if (
      vechileData?.data?.data !== null &&
      vechileData?.data?.data?.vehicleConfigViews.length > 0
    ) {
      const data = vechileData.data?.data?.vehicleConfigViews.map(
        (item: any, index: number) => ({
          ...item,
          id: index + 1,
          vehicleNumber: item.vehicleNumber.toUpperCase(),
          services: item.services !== null ? item.services : '-',
          tripModesJoin: item?.tripModes?.length > 0 && item?.tripModes.join(', ')
        })
      );
      setVechileRows(data);
    } else setVechileRows([]);
  }, [vechileData?.data]);

  return (
    <Box>
      {!APoperator && (
        <Box className='agent-header-btn'>
          <CustomButton
            className='saveChanges'
            category='Add Vehicle'
            onClick={() => {
              setOpen(true);
              setSelectedRow(null);
              //   setselectedAgents(null);
            }}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'right', margin: '0', width: '100%' }}>
        <Box sx={{ width: 200 }}>
          <CustomTextField
            name='search'
            control={control}
            id='filter-input'
            placeholder='Search....'
            value={filter}
            variant='standard'
            icon={<SearchOutlinedIcon color='primary' />}
            onChangeCallback={(e: any) => handleFilterChange(e)}
          />
        </Box>
      </Box>
      <CustomDataGrid
        rows={vechileRows}
        loading={vechileData?.isLoading}
        columns={columns}
        rowCount={vechileData?.data?.data?.count}
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={(id, row) => {
          if (row?.isActive === 1) {
            handleUpdateVehicle();
          }

          setSelectedRow(row);
        }}
        type='logistics'
        pageNo={pageNo}
        pageSize={pageSize}
        sortingMode='server'
        onSortModelChange={handleSortModelChange}
        paginationModel={{
          page: pageNo ? pageNo - 1 : 0,
          pageSize: pageSize ? pageSize : 10
        }}
      />

      <Dialog open={drawer} className='driver-dialog'>
        <Box className='driver-view'>
          <Box className='driver-view-header'>
            <Typography
              variant='h6'
              sx={{
                color: '#333',
                fontWeight: 600
              }}
            >
              Driver
            </Typography>
            <Box className='driver-close' onClick={() => setDrawer(false)}>
              <Icon icon='ic:round-close' className='driver-close-icon' />
            </Box>
          </Box>
          <Box className='driver-details'>
            {selectedRow?.driverDetails && selectedRow?.driverDetails?.length > 0 ? (
              <Grid container spacing={2} className='driver-details-values'>
                <Grid item sm={6} className='driver-values head'>
                  Name
                </Grid>
                <Grid item sm={6} className='driver-values head'>
                  Contact No
                </Grid>
              </Grid>
            ) : (
              ''
            )}
            {selectedRow?.driverDetails && selectedRow.driverDetails.length > 0 ? (
              selectedRow.driverDetails.map((driver: any, index: any) => (
                <Grid container key={index} spacing={2} className='driver-details-values'>
                  <Grid item sm={6} className='driver-values'>
                    {index + 1}. {driver.driverName}
                  </Grid>
                  <Grid item sm={6} className='driver-values'>
                    {driver.contactPhone}
                  </Grid>
                </Grid>
              ))
            ) : (
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#777',
                    textAlign: 'center',
                    marginBottom: '15px'
                  }}
                >
                  No driver assigned
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>
      {open && (
        <AddVehicle
          setOpen={setOpen}
          open={open}
          selectedRow={selectedRow}
          payloads={payload}
        />
      )}

      {/* Deactivate Popup  */}
      {isDialog === 'dialog' ? (
        <Dialog open={isDialog === 'dialog'}>
          <Box
            sx={{
              textAlign: 'center',
              padding: '5%',
              // width: '350px'
              minWidth: '350px'
            }}
          >
            <Typography>
              {selectedRow.isActive === 1
                ? isAPadminOrOperator
                  ? constant.VehicleDeleteConfirmation
                  : constant.Deactivate
                : constant.Activate}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                paddingTop: '5%',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ marginRight: '12px' }}>
                <CustomButton
                  className='saveChanges'
                  loading={deactivateLoading}
                  category='Yes'
                  onClick={handleDeactivate}
                />
              </Box>
              <CustomButton
                className='cancel'
                category='No'
                onClick={() => {
                  setIsDialog('');
                }}
              />
            </Box>
          </Box>
        </Dialog>
      ) : (
        <ConfirmationPopup
          open={isDialog === 'dialog'}
          loading={deactivateLoading}
          onClose={() => {
            setIsDialog('');
          }}
          onConfirm={handleDeactivate}
          title='Deactivate Vehicle'
          messages={[
            'You are about to deactivate this vehicle.',
            'This action may affect scheduled trips and vehicle availability.'
          ]}
          confirmText='Yes, Deactivate'
          cancelText='Cancel'
        />
      )}

      {/* Delete Popup */}
      {isDialog === 'Delete' && (
        <Dialog open={isDialog === 'Delete'}>
          <Box
            sx={{
              textAlign: 'center',
              padding: '5%',
              minWidth: '350px'
            }}
          >
            <Typography>
              {isAPadminOrOperator
                ? constant.VehicleDeleteConfirmation
                : constant.DeleteConfirmation}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                paddingTop: '5%',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ marginRight: '12px' }}>
                <CustomButton
                  className='saveChanges'
                  category='Yes'
                  loading={deleteLoading}
                  onClick={() => {
                    handleDeleteVehicle();
                  }}
                />
              </Box>
              <CustomButton
                className='cancel'
                category='No'
                onClick={() => setIsDialog('')}
              />
            </Box>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default VehicleOnboard;
